import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { T, useT } from "@transifex/react";
import { zodResolver } from "storybook/utils/form";
import { NAVIGATOR_HOSTNAME, SPECIALIST_HOSTNAME } from "env-vars";
import { AvailabilityPicker } from "components/availability-picker";
import { usePatient } from "context/advert/use-patient";
import { formatPhone } from "helpers";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  FormSection,
  SectionTitle,
  InputGroup,
  InputWrapper,
  Label,
  Input,
  ReadOnlyValue,
  ModalFooter,
  CancelButton,
  SubmitButton,
  ErrorMessage,
  DisclaimerText,
  SuccessMessage,
  SuccessIcon,
  SuccessTitle,
  SuccessText,
} from "./styles";
import {
  availabilitySchema,
  type AvailabilityFormType,
  type DayAvailabilityType,
} from "./availability-schema";

/**
 * Maps a riskhub item's `interest` string to the specialist PersonStatus
 * `risk_guideline` enum integer.
 *
 * Specialist enum (from person_status.rb):
 *   breast: 0, prostate: 1, cervical: 2, colon: 3,
 *   lung: 4, mri: 5, counseling: 6, testing: 7
 */
const INTEREST_TO_RISK_GUIDELINE: Record<string, number> = {
  testing: 7,
  genetic_testing: 7,
  mri: 5,
  breast_mri: 5,
  lung: 4,
  lung_ct: 4,
  counseling: 6,
  genetic_counseling: 6,
};

/** Risk guidelines that historically route to the navigator service */
const NAVIGATOR_GUIDELINES = new Set([7]); // testing

function normalizeInterest(interest: string | undefined): string {
  return String(interest ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function resolveRiskGuideline(interest: string | undefined): number | null {
  const key = normalizeInterest(interest);
  return INTEREST_TO_RISK_GUIDELINE[key] ?? null;
}

// ── Payload builder ──────────────────────────────────────────────────

interface SelectedCard {
  /** The riskhub item's `interest` attribute value (e.g. "testing", "mri") */
  interest: string;
}

function getDayAvailability(day: DayAvailabilityType): ("AM" | "PM")[] | undefined {
  const slots: ("AM" | "PM")[] = [];
  if (day.enabled && day.am) slots.push("AM");
  if (day.enabled && day.pm) slots.push("PM");
  return slots.length > 0 ? slots : undefined;
}

function buildAvailabilityPayload(form: AvailabilityFormType) {
  return {
    start_time: Date.now(),
    stop_time: Date.now() + 360000,
    availability: {
      anytime: !!form.anytime,
      monday: getDayAvailability(form.monday),
      tuesday: getDayAvailability(form.tuesday),
      wednesday: getDayAvailability(form.wednesday),
      thursday: getDayAvailability(form.thursday),
      friday: getDayAvailability(form.friday),
    },
  };
}

const IN_PERSON_INTERESTED_STATUS = 21;
const PATIENT_PORTAL_METHOD = 4;

interface ContactInfo {
  email?: string;
  phone?: string;
}

function buildPersonPayload(
  guidelines: number[],
  availability: ReturnType<typeof buildAvailabilityPayload> | null,
  contactInfo?: ContactInfo
) {
  const currentTime = Math.floor(Date.now());

  const contactAttributes: Record<string, string> = {};
  if (contactInfo?.email) {
    contactAttributes.email = contactInfo.email;
  }
  if (contactInfo?.phone) {
    contactAttributes.cell_phone = contactInfo.phone.replace(/\D/g, "");
  }

  return {
    person: {
      ...(availability ? { replace_appointments: true } : {}),
      person_statuses_attributes: guidelines.map((rg) => ({
        status: IN_PERSON_INTERESTED_STATUS,
        risk_guideline: rg,
      })),
      ...(availability
        ? { preferred_appointment_times_attributes: [availability] }
        : {}),
      ...(Object.keys(contactAttributes).length > 0
        ? { contact_attributes: contactAttributes }
        : {}),
      person_contact_histories_attributes: guidelines.map((rg) => ({
        actionDateTime: currentTime,
        in_person_reason: 1,
        risk_guideline: rg,
        method: PATIENT_PORTAL_METHOD,
      })),
    },
  };
}

// ── Contact Info Source ───────────────────────────────────────────────

export interface StoredContactInfo {
  email: string | null;
  phone: string | null;
}

export type ContactInfoSource = () => StoredContactInfo;

// ── Component ────────────────────────────────────────────────────────

export interface SimpleRequestCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The riskhub cards the user selected for follow-up */
  selectedCards: SelectedCard[];
  /**
   * When true all guidelines go to the specialist service (navigator is not used).
   * Matches the `removeAccessToNavigatorApp` feature flag.
   */
  specialistOnly?: boolean;
  /** Custom modal title. Falls back to "Request a Call" when empty/undefined. */
  title?: string;
  /** Whether to show the availability picker. Defaults to true. */
  showAvailability?: boolean;
  /**
   * Custom source for stored contact info. Defaults to reading from
   * localStorage (email) and patient context (phone).
   * Useful for testing without mocking globals.
   */
  getStoredContactInfo?: ContactInfoSource;
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
      fill="currentColor"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
      fill="currentColor"
    />
  </svg>
);

const defaultDay = { enabled: false, am: false, pm: false };

export function SimpleRequestCallModal({
  isOpen,
  onClose,
  selectedCards,
  specialistOnly = false,
  title,
  showAvailability = true,
  getStoredContactInfo,
}: SimpleRequestCallModalProps) {
  const t = useT();
  const { patient } = usePatient();
  const displayTitle = title?.trim() || t("Request a Call");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [usedExistingContact, setUsedExistingContact] = useState(false);

  // Contact info state (for when showAvailability is false)
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Default contact info source reads from localStorage and patient context
  const defaultGetStoredContactInfo = useCallback((): StoredContactInfo => ({
    email: typeof window !== "undefined" ? localStorage.getItem("ciq_email") : null,
    phone: patient?.phone_number ?? null,
  }), [patient?.phone_number]);

  const storedContact = (getStoredContactInfo ?? defaultGetStoredContactInfo)();
  const storedEmail = storedContact.email;
  const patientPhone = storedContact.phone;
  const hasExistingContact = !!(storedEmail || patientPhone);

  const form = useForm<AvailabilityFormType>({
    resolver: zodResolver(availabilitySchema),
    defaultValues: {
      anytime: false,
      monday: defaultDay,
      tuesday: defaultDay,
      wednesday: defaultDay,
      thursday: defaultDay,
      friday: defaultDay,
    },
  });

  const { handleSubmit } = form;

  // Prefill contact info when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (storedEmail) setEmail(storedEmail);
    if (patientPhone) setPhone(formatPhone(patientPhone) || "");
  }, [isOpen, storedEmail, patientPhone]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsSuccess(false);
      setUsedExistingContact(false);
      form.reset();
      setEmail("");
      setPhone("");
    }
  }, [isOpen, form]);

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhone(formatPhone(e.target.value) || "");
    },
    []
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
    },
    []
  );

  const handleFormSubmit = useCallback(
    async (data: AvailabilityFormType) => {
      setError(null);

      // Validate: need at least one contact method (existing or new)
      const hasAnyEmail = storedEmail || email;
      const hasAnyPhone = patientPhone || phone;
      if (!hasAnyEmail && !hasAnyPhone) {
        setError(t("Please provide an email address or phone number."));
        return;
      }
      // Validate new email format if provided
      if (email && !storedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError(t("Please enter a valid email address."));
        return;
      }

      setIsSubmitting(true);

      try {
        const patientId = localStorage.getItem("ciq_id") || "";
        const idToken = localStorage.getItem("id_token");
        const availability = showAvailability
          ? buildAvailabilityPayload(data)
          : null;

        // Partition guidelines into specialist vs navigator
        const specialistGuidelines: number[] = [];
        const navigatorGuidelines: number[] = [];

        selectedCards.forEach((card) => {
          const rg = resolveRiskGuideline(card.interest);
          if (rg === null) return;

          if (!specialistOnly && NAVIGATOR_GUIDELINES.has(rg)) {
            navigatorGuidelines.push(rg);
          } else {
            specialistGuidelines.push(rg);
          }
        });

        const fetchPromises: Promise<Response>[] = [];

        if (specialistGuidelines.length > 0) {
          // Include new contact info (only fields not already stored)
          const newEmail = !storedEmail && email ? email : undefined;
          const newPhone = !patientPhone && phone ? phone : undefined;
          const contactInfo: ContactInfo | undefined =
            newEmail || newPhone
              ? { email: newEmail, phone: newPhone }
              : undefined;

          fetchPromises.push(
            fetch(`${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify(
                buildPersonPayload(specialistGuidelines, availability, contactInfo)
              ),
            })
          );
        }

        if (navigatorGuidelines.length > 0) {
          const navPayload = {
            person: {
              ...(availability
                ? {
                    replace_appointments: true,
                    preferred_appointment_times_attributes: [availability],
                  }
                : {}),
              person_statuses_attributes: navigatorGuidelines.map((rg) => ({
                status: IN_PERSON_INTERESTED_STATUS,
                risk_guideline: rg,
              })),
              person_contact_histories_attributes: navigatorGuidelines.map(
                (rg) => ({
                  actionDateTime: Math.floor(Date.now()),
                  actionType: 17,
                  in_person_reason: 1,
                  risk_guideline: rg,
                })
              ),
            },
          };

          fetchPromises.push(
            fetch(`${NAVIGATOR_HOSTNAME}/api/v1/people/${patientId}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify(navPayload),
            })
          );
        }

        if (fetchPromises.length === 0) {
          setIsSuccess(true);
          return;
        }

        const responses = await Promise.all(fetchPromises);

        if (responses.some((r) => !r.ok)) {
          setError(t("Unable to submit your request. Please try again."));
          return;
        }

        // Track if we used ONLY existing contact (both fields read-only) for success message
        if (storedEmail && patientPhone) {
          setUsedExistingContact(true);
        }
        setIsSuccess(true);
      } catch (err) {
        console.error("Error submitting availability:", err);
        setError(t("Unable to submit your request. Please try again."));
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedCards, specialistOnly, showAvailability, storedEmail, patientPhone, email, phone, t]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  // Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Lock scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (isSuccess) {
    const successMessage = usedExistingContact
      ? t("A representative will contact you using the contact information from your record. Thank you!")
      : showAvailability
        ? t("A representative will contact you during your selected availability. Thank you!")
        : t("A representative will contact you soon. Thank you!");

    return (
      <ModalOverlay onClick={handleOverlayClick} role="dialog" aria-modal="true">
        <ModalContainer>
          <CloseButton
            onClick={onClose}
            aria-label={t("Close")}
            data-testid="SimpleRequestCallModal__CloseButton"
          >
            <CloseIcon />
          </CloseButton>
          <SuccessMessage data-testid="SimpleRequestCallModal__Success">
            <SuccessIcon>
              <CheckIcon />
            </SuccessIcon>
            <SuccessTitle>
              <T _str="Request Submitted" />
            </SuccessTitle>
            <SuccessText>
              {successMessage}
            </SuccessText>
          </SuccessMessage>
          <ModalFooter>
            <SubmitButton onClick={onClose}>
              <T _str="Done" />
            </SubmitButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="simple-request-call-title"
      data-testid="SimpleRequestCallModal"
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle id="simple-request-call-title">
            {displayTitle}
          </ModalTitle>
          <ModalSubtitle>
            {showAvailability ? (
              <T _str="Please confirm your contact information and indicate the best times for your care team to reach out." />
            ) : storedEmail && patientPhone ? (
              <T _str="We'll use the contact information from your record to reach out to you." />
            ) : (
              <T _str="Please confirm your contact information so our care team can reach out to you." />
            )}
          </ModalSubtitle>
          <CloseButton
            onClick={onClose}
            aria-label={t("Close")}
            data-testid="SimpleRequestCallModal__CloseButton"
          >
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormProvider {...form}>
            <form
              aria-label="availability-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(handleFormSubmit)();
              }}
              data-testid="SimpleRequestCallModal__Form"
            >
              <FormSection>
                <SectionTitle>
                  <T _str="Contact Information" />
                </SectionTitle>
                <InputGroup>
                  <InputWrapper>
                    <Label htmlFor="simple-request-call-email">
                      <T _str="Email Address" />
                    </Label>
                    {storedEmail ? (
                      <ReadOnlyValue data-testid="SimpleRequestCallModal__EmailReadOnly">
                        {storedEmail}
                      </ReadOnlyValue>
                    ) : (
                      <Input
                        id="simple-request-call-email"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder={t("your@email.com")}
                        disabled={isSubmitting}
                        data-testid="SimpleRequestCallModal__EmailInput"
                      />
                    )}
                  </InputWrapper>
                  <InputWrapper>
                    <Label htmlFor="simple-request-call-phone">
                      <T _str="Phone Number" />
                    </Label>
                    {patientPhone ? (
                      <ReadOnlyValue data-testid="SimpleRequestCallModal__PhoneReadOnly">
                        {formatPhone(patientPhone)}
                      </ReadOnlyValue>
                    ) : (
                      <Input
                        id="simple-request-call-phone"
                        type="tel"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder={t("(555) 123-4567")}
                        disabled={isSubmitting}
                        data-testid="SimpleRequestCallModal__PhoneInput"
                      />
                    )}
                  </InputWrapper>
                </InputGroup>
              </FormSection>

              {showAvailability && (
                <FormSection>
                  <SectionTitle>
                    <T _str="Preferred Availability" />
                  </SectionTitle>
                  <DisclaimerText data-testid="SimpleRequestCallModal__Disclaimer">
                    <T _str="Please note: We will do our best to accommodate your preferred times, but availability may vary and we cannot guarantee a specific time slot." />
                  </DisclaimerText>
                  <AvailabilityPicker
                    namePrefix=""
                    theme="patient-facing"
                    noContainer
                  />
                </FormSection>
              )}

              {error && (
                <ErrorMessage data-testid="SimpleRequestCallModal__Error">
                  {error}
                </ErrorMessage>
              )}

              <ModalFooter>
                <CancelButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  data-testid="SimpleRequestCallModal__CancelButton"
                >
                  <T _str="Cancel" />
                </CancelButton>
                <SubmitButton
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="SimpleRequestCallModal__SubmitButton"
                >
                  {isSubmitting ? t("Submitting...") : t("Submit")}
                </SubmitButton>
              </ModalFooter>
            </form>
          </FormProvider>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
}
